import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Copy from 'react-feather/dist/icons/copy';
import X from 'react-feather/dist/icons/x';
import { copyAsCSV } from '../../../utils/utils';
import { CONSTANTS, FEATURE_FLAGS } from '../../../constants/constants';
import NRRemediateIcon from '../../../NRIcons/NRRemediateIcon';
import React, { useContext, useEffect, useState } from 'react';
import { useFlag } from '@openfeature/react-sdk';
import { RemediationContext } from '../../NRRemediation/NRRemediationContext';
import { useTheme, alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GridContext } from '../../NRReactGrid/NRReactGridContext';
import { useSnackbar } from 'notistack';
import FileDownloadComponent from '../../NRCertTable/CertTableCustomComponents/FileDownloadComponent';
import NRGroupIcon from '../../../NRIcons/NRGroupIcon';
import NRRemoveGroupsIcon from '../../../NRIcons/NRRemoveGroupsIcon';
import { AssetGroupContext } from '../../AssetGroup/assetGroupContextWrapper';
import { SBOMContext } from '../../../pages/NRSBOMTable/NRSBOMDataShare';
import useHasPermission from '../../Permissions/useHasPermission';
import { useLocation, useParams } from 'react-router-dom';
import NRSelectAll from '../NRSelectAll/NRSelectAll';
import WithPermissions from '../../Permissions/WithPermissions';
import NRCreateReportModal from '../../../pages/NRReports/NRCreateReportModal';
import NRReportIcon from '../../../NRIcons/NRReportIcon';
import { SBOM_CONSTANTS } from '../../SBOM/SBOM.constants';
import NRRemediationContent from '../../NRRemediation/NRRemediationContent/NRRemediationContent';
import NRLicenseIssueRemediationContent from '../../NRRemediation/NRLicenseIssueRemediation/NRLicenseIssueRemediationContent';
import NRSecretRemediationContent from '../../NRSecretDetection/remediations/NRSecretRemediationContent';
import NRCryptographyRemediationContent from '../../NRRemediation/NRCryptographyRemediation/NRCryptographyRemediationContent';
import { CERTIFICATES, REMEDIATE_CERTIFICATES } from '../../../graphql/queries/cryptography';
import { GET_PRIVATE_KEY, REMEDIATE_PRIVATE_KEYS } from '../../../graphql/queries/privateKey';
import { RBAC_PERMISSIONS } from '../../../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { ACR_RESOURCE_TYPE } from '../../../pages/NRSettings/NRUsersAndAccess/rbac.constants';

const {
  ITEM_SELECTED,
  ITEMS_SELECTED,
  TOOLTIP_COPY_CSV,
  ADD_TO_GROUPS,
  REMOVE_FROM_GROUPS,
  TOOLTIP_UNSELECT,
  TOOLTIP_REMEDIATE,
  TOOLTIP_DOWNLOAD,
  TOOLTIP_CREATE_REPORT,
  TOOLTIP_CREATE_REPORT_DISABLED
} = CONSTANTS.TABLE.COPY_TO_CLIPBOARD_BANNER;
const {
  VULNERABILITIES: VULNERABILITIES_TAB,
  FILE_SYSTEM: FILE_SYSTEM_TAB,
  SBOM: SBOM_TAB,
  LICENSE_ISSUES: LICENSE_ISSUES_TAB,
  CERTIFICATES: CERTIFICATES_TAB,
  PRIVATE_KEYS: PRIVATE_KEYS_TAB
} = CONSTANTS.REPORT.TABS;
const { EXPOSED_SECRETS } = CONSTANTS.REPORT.CREDENTIALS_SUB_TABS;

const addToGroupsValidPaths = ['/assets'];

function CopyToClipboardBanner({ selectedItems, setSelection, rows, totalCount, showSelectAllItems, selectAllFetchConfig }) {
  const { setOpenRemediationModal, setRows, setRemediateAllVulnerabilitiesFilter, setContent, setTitle } = useContext(RemediationContext);
  const { componentId: assetId } = useParams();
  const { setShouldClearSelection } = useContext(GridContext);
  const { enqueueSnackbar } = useSnackbar();
  const [filesToDownload, setFilesToDownload] = useState();
  const [disableDownload, setDisableDownload] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const theme = useTheme();
  const { setSelectedAssets, setShowManageGroupsAddBulkModal, setShowManageGroupsRemoveAllModal } = useContext(AssetGroupContext);
  const { setOpenRemoveModal, setSelectedComponents } = useContext(SBOMContext);
  const canWriteGroups = useHasPermission({ permission: CONSTANTS.PERMISSIONS.WRITE_GROUPS });
  const location = useLocation();
  const { tab, subTab } = useParams();
  const pathname = location.pathname;
  const showAddToGroupOptions = addToGroupsValidPaths.includes(pathname);
  const { value: isReportsEnabled } = useFlag(FEATURE_FLAGS.ASSET_DIFFING, false);
  const [isCreateReportModalOpen, setIsCreateReportModalOpen] = useState(false);

  const handleRemediationClick = () => {
    if (tab === VULNERABILITIES_TAB) {
      let cvesToRemediate = !!allItems.length ? allItems : selectedItems.map(item => rows[item]);
      setRows(cvesToRemediate);
      if (!!allItems.length && selectAllFetchConfig?.qlArgs) {
        setRemediateAllVulnerabilitiesFilter(selectAllFetchConfig?.qlArgs);
      }
      setTitle(`Remediate issues - ${cvesToRemediate?.length}`);
      setContent(<NRRemediationContent />);
    } else if (tab === LICENSE_ISSUES_TAB) {
      let licensesToRemediate = !!allItems.length ? allItems : selectedItems.map(item => rows[item]);
      setTitle(`Remediate issues - ${licensesToRemediate?.length}`);
      setContent(<NRLicenseIssueRemediationContent licenseIssues={licensesToRemediate} />);
    } else if (subTab === EXPOSED_SECRETS) {
      let secretsToRemediate = !!allItems.length ? allItems : selectedItems.map(item => rows[item]);
      setTitle(`Remediate secrets - ${secretsToRemediate?.length}`);
      setContent(<NRSecretRemediationContent secrets={secretsToRemediate} />);
    } else if (subTab === CERTIFICATES_TAB) {
      let certificatesToRemediate = !!allItems.length ? allItems : selectedItems.map(item => rows[item]);
      setTitle(`Remediate certificates - ${certificatesToRemediate?.length}`);
      setContent(
        <NRCryptographyRemediationContent
          items={certificatesToRemediate}
          mutation={REMEDIATE_CERTIFICATES}
          mutationName="remediateCertificates"
          refetchQuery={CERTIFICATES}
          itemTypeLabel="Certificate"
          itemType="certificates"
          successMessage="Status changed for ${0} certificate(s)."
        />
      );
    } else if (subTab === PRIVATE_KEYS_TAB) {
      let privateKeysToRemediate = !!allItems.length ? allItems : selectedItems.map(item => rows[item]);
      setTitle(`Remediate private keys - ${privateKeysToRemediate?.length}`);
      setContent(
        <NRCryptographyRemediationContent
          items={privateKeysToRemediate}
          mutation={REMEDIATE_PRIVATE_KEYS}
          mutationName="remediatePrivateKeys"
          refetchQuery={GET_PRIVATE_KEY}
          itemTypeLabel="Private Key"
          itemType="privateKeys"
          successMessage="Status changed for ${0} private key(s)."
        />
      );
    }
    setOpenRemediationModal(true);
  };

  const handleAddToGroupsClick = () => {
    let assetsToAdd = !!allItems.length ? allItems : rows.filter((e, index) => selectedItems.includes(index));
    setSelectedAssets(assetsToAdd);
    setShowManageGroupsAddBulkModal(true);
  };

  useEffect(() => {
    return () => setFilesToDownload(null);
  }, []);

  useEffect(() => {
    if (tab === FILE_SYSTEM_TAB) {
      handleBulkDownload();
    }
  }, [selectedItems]);

  const handleBulkDownload = () => {
    let toReturn = selectedItems.map(item => ({ path: rows[item]?.path }));
    setDisableDownload(selectedItems.some(item => !rows[item]?.canDownload));
    setFilesToDownload(toReturn);
  };

  async function handleCopyCsvClick() {
    copyAsCSV(selectedItems.map(item => rows[item]));
    enqueueSnackbar(CONSTANTS.COPIED_TO_CLIPBOARD_MSG, { variant: 'success' });
    setShouldClearSelection(true);
  }

  function getIdentificationsFromDependencies(data) {
    if (!Array.isArray(data)) {
      return [];
    }

    const identificationsArray = data.flatMap(item => {
      if (item?.dependency?.type === SBOM_CONSTANTS.COMPONENT_TYPE.ROOT) {
        return [];
      }
      return item?.dependency?.identificationIds || [];
    });

    return identificationsArray;
  }

  const handleRemoveComponentsClick = () => {
    const selectedRows = rows.filter((e, index) => selectedItems.includes(index));
    const identificationIds = getIdentificationsFromDependencies(selectedRows);
    const combinedComponents = { assetId, identificationIds, count: selectedRows?.length };

    setSelectedComponents(combinedComponents);
    setOpenRemoveModal(true);
  };

  return (
    <Box
      sx={{
        height: 40,
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0.5, 2)
      }}
    >
      {!allItems.length && (
        <Typography variant="body2" color="inherit">
          {selectedItems.length + ' '}
          {selectedItems.length > 1 ? ITEMS_SELECTED : ITEM_SELECTED}
        </Typography>
      )}
      {!!allItems.length && (
        <Typography variant="body2" color="inherit">
          {allItems.length > 1 ? 'All ' : ''}
          {allItems.length + ' '}
          {allItems.length > 1 ? ITEMS_SELECTED.toLowerCase() : ITEM_SELECTED.toLowerCase()}
        </Typography>
      )}
      {!!selectAllFetchConfig && showSelectAllItems && showAddToGroupOptions && (
        <NRSelectAll allItems={allItems} setAllItems={setAllItems} itemsCount={totalCount} {...selectAllFetchConfig} />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {showAddToGroupOptions && isReportsEnabled && (
          <Tooltip title={selectedItems.length !== 2 ? TOOLTIP_CREATE_REPORT_DISABLED : TOOLTIP_CREATE_REPORT}>
            <span>
              <IconButton
                aria-label="Create Report"
                component="span"
                color="inherit"
                size="small"
                disabled={selectedItems.length !== 2}
                onClick={() => setIsCreateReportModalOpen(true)}
                sx={{
                  color: 'inherit',
                  '&.Mui-disabled': {
                    color: theme.palette.secondary.contrastText,
                    opacity: theme.palette.action.disabledOpacity
                  }
                }}
              >
                <NRReportIcon color="currentColor" />
                <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1, mr: 1 }}>
                  Create Report
                </Typography>
              </IconButton>
            </span>
          </Tooltip>
        )}
        {(tab === VULNERABILITIES_TAB ||
          tab === LICENSE_ISSUES_TAB ||
          subTab === EXPOSED_SECRETS ||
          subTab === CERTIFICATES_TAB ||
          subTab === PRIVATE_KEYS_TAB) && (
          <WithPermissions
            permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}
            rbacPermission={RBAC_PERMISSIONS.FINDINGS_REMEDIATE}
            resourceType={ACR_RESOURCE_TYPE.ASSET}
          >
            <Tooltip title={TOOLTIP_REMEDIATE}>
              <IconButton
                aria-label="Remediate items"
                component="span"
                color="inherit"
                size="small"
                onClick={handleRemediationClick}
                sx={{
                  color: 'inherit'
                }}
              >
                <NRRemediateIcon color="currentColor" />
                <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1, mr: 1 }}>
                  Remediate
                </Typography>
              </IconButton>
            </Tooltip>
          </WithPermissions>
        )}
        {tab === FILE_SYSTEM_TAB && (
          <WithPermissions
            permission={CONSTANTS.PERMISSIONS.READ_IMGS}
            rbacPermission={RBAC_PERMISSIONS.DOWNLOAD_ASSETS}
            resourceType={ACR_RESOURCE_TYPE.ASSET}
          >
            <FileDownloadComponent
              componentId={assetId}
              fileList={filesToDownload}
              cursorPointer={true}
              tooltipTitle={TOOLTIP_DOWNLOAD}
              color="inherit"
              showButtonComponent={true}
              afterDownloadAction={() => setShouldClearSelection(true)}
              disabled={disableDownload}
              sx={{
                color: 'inherit'
              }}
            />
          </WithPermissions>
        )}
        {!!canWriteGroups && showAddToGroupOptions && (
          <>
            <Tooltip title={ADD_TO_GROUPS}>
              <IconButton
                aria-label="Add to Groups"
                component="span"
                color="inherit"
                size="small"
                onClick={() => handleAddToGroupsClick()}
                sx={{
                  color: 'inherit'
                }}
              >
                <NRGroupIcon color="currentColor" />
                <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1, mr: 1 }}>
                  Add to Groups
                </Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title={REMOVE_FROM_GROUPS}>
              <IconButton
                aria-label="Remove from Groups"
                component="span"
                color="inherit"
                size="small"
                onClick={() => {
                  setSelectedAssets(rows.filter((e, index) => selectedItems.includes(index)));
                  setShowManageGroupsRemoveAllModal(true);
                }}
                sx={{
                  color: 'inherit'
                }}
              >
                <NRRemoveGroupsIcon color="currentColor" />
                <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1, mr: 1 }}>
                  Remove from Groups
                </Typography>
              </IconButton>
            </Tooltip>
          </>
        )}
        {tab === SBOM_TAB && (
          <WithPermissions permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}>
            <Tooltip title={'Remove Components'}>
              <IconButton
                aria-label="Remove Components"
                component="span"
                color="inherit"
                size="small"
                onClick={handleRemoveComponentsClick}
                sx={{
                  color: 'inherit'
                }}
              >
                <NRRemoveGroupsIcon color="currentColor" />
                <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1, mr: 1 }}>
                  Remove Components
                </Typography>
              </IconButton>
            </Tooltip>
          </WithPermissions>
        )}
        <Tooltip title={TOOLTIP_COPY_CSV}>
          <IconButton
            aria-label="copy to csv"
            component="span"
            color="inherit"
            size="small"
            onClick={() => handleCopyCsvClick()}
            sx={{
              color: 'inherit'
            }}
          >
            <Copy color="currentColor" />
            <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1, mr: 1 }} children={TOOLTIP_COPY_CSV} />
          </IconButton>
        </Tooltip>
        <Tooltip title={TOOLTIP_UNSELECT}>
          <IconButton
            aria-label="unselect"
            component="span"
            color="inherit"
            size="small"
            onClick={() => {
              setSelection([]);
              setAllItems([]);
            }}
            sx={{
              color: 'inherit'
            }}
          >
            <X color="currentColor" />
            <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1 }}>
              Cancel
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>
      {isCreateReportModalOpen && (
        <NRCreateReportModal
          open={isCreateReportModalOpen}
          onClose={() => setIsCreateReportModalOpen(false)}
          onReportCreated={() => setShouldClearSelection(true)}
          initialAssets={selectedItems.map(item => rows[item])}
        />
      )}
    </Box>
  );
}
export default CopyToClipboardBanner;
